import clsx from 'clsx';
import type * as React from 'react';

import { OrderDirection } from '@repo/frontend-shared';

import type { SDSTableAlign } from './types/table.types';

interface SDSTableHeaderProps extends React.ThHTMLAttributes<HTMLTableCellElement> {
  children: React.ReactNode;
  sortable?: boolean;
  sortDirection?: OrderDirection | false;
  align?: SDSTableAlign;
  onSort?: () => void;
  ref?: React.Ref<HTMLTableCellElement>;
}

export function SDSTableHeader({
  children,
  sortable = false,
  sortDirection,
  align = 'left',
  onSort,
  className,
  onClick,
  ref,
  ...props
}: SDSTableHeaderProps) {
  const handleClick = (event: React.MouseEvent<HTMLTableCellElement>) => {
    if (sortable && onSort) {
      onSort();
    }
    onClick?.(event);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTableCellElement>) => {
    if (sortable && onSort && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      onSort();
    }
  };

  return (
    <th
      ref={ref}
      className={clsx(
        'SDSTableHeader',
        `SDSTableHeader--align-${align}`,
        {
          'SDSTableHeader--sortable': sortable,
          'SDSTableHeader--sorted-asc': sortDirection === OrderDirection.ASC,
          'SDSTableHeader--sorted-desc': sortDirection === OrderDirection.DESC,
        },
        className,
      )}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      tabIndex={sortable ? 0 : undefined}
      role={sortable ? 'columnheader button' : 'columnheader'}
      aria-sort={
        sortDirection === OrderDirection.ASC
          ? 'ascending'
          : sortDirection === OrderDirection.DESC
            ? 'descending'
            : sortable
              ? 'none'
              : undefined
      }
      {...props}
    >
      <div className="SDSTableHeader__content">
        {children}
        {sortable && (
          <span className="SDSTableHeader__sort-icon" aria-hidden="true">
            {sortDirection === OrderDirection.ASC ? '↑' : sortDirection === OrderDirection.DESC ? '↓' : '↕'}
          </span>
        )}
      </div>
    </th>
  );
}
